import { Suspense, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { TabBar } from './TabBar';
import { TenantDrawer } from './TenantDrawer';
import { useUiStore } from '@shared/stores/uiStore';
import { useMemberProfile } from '@shared/hooks/useMemberProfile';
import { useSessionRefresh } from '@shared/hooks/useSessionRefresh';
import { useTabBarHidden } from '@shared/hooks/useTabBarHidden';
import { useHideOnScroll } from '@shared/hooks/useHideOnScroll';
import { ScreenLoader } from '@shared/ui/feedback/ScreenLoader';

/**
 * TabShell — authenticated app frame. There is no persistent top bar:
 * every screen is full-bleed and paints its own header, which stays pinned
 * (`sticky top-0`) while the content scrolls beneath it.
 *
 * Page changes are instant — no transition animation. The scroll
 * container is keyed by pathname so each route mounts fresh and scroll
 * resets to the top.
 */
export function TabShell(): JSX.Element {
  const location = useLocation();
  const navDrawerOpen = useUiStore((s) => s.navDrawerOpen);
  const setNavDrawerOpen = useUiStore((s) => s.setNavDrawerOpen);
  const trackPath = useUiStore((s) => s.trackPath);
  const tabBarHidden = useTabBarHidden();
  // Hide the bar while scrolling down, reveal on scroll-up. Disabled where the bar
  // is already removed, so the two hiding rules compose instead of fighting.
  const { hidden: navHiddenByScroll, containerRef } = useHideOnScroll(!tabBarHidden);
  useMemberProfile();
  useSessionRefresh();

  useEffect(() => {
    trackPath(location.pathname);
  }, [location.pathname, trackPath]);

  return (
    <div className="relative flex h-full flex-col overflow-hidden bg-paper">
      <main
        key={location.pathname}
        ref={containerRef}
        className={tabBarHidden
          ? 'flex-1 overflow-y-auto overflow-x-hidden overscroll-y-none scrollbar-none bg-paper'
          : 'flex-1 overflow-y-auto overflow-x-hidden overscroll-y-none scrollbar-none bg-paper pb-24'}
      >
        {/* Route screens are code-split — the fallback stays inside <main> so the tab bar
            and drawer survive the chunk load instead of flashing away with the screen. */}
        <Suspense fallback={<ScreenLoader />}>
          <Outlet />
        </Suspense>
      </main>
      {tabBarHidden ? null : <TabBar hidden={navHiddenByScroll} />}
      <TenantDrawer open={navDrawerOpen} onOpenChange={setNavDrawerOpen} />
    </div>
  );
}
